import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";

function NotificationAttachmentTemplate() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const [templatesData, setTemplatesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [navFormData, setNavFormData] = useState([]);
  const [formEventsData, setFormEventsData] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      id: 0,
      notificationAttachmentTemplate: {
        title: "",
        templateHTML: "",
        formId: "",
        notificationFormEventId: "",
        fileType: "",
      },
    },
  });

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };

  // Fetch categories
  const getCategories = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("UserAuthentication/GetAllCategory", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.navCategory) {
        setCategoryData(response.navCategory);
      } else {
        toast.error("Failed to fetch categories.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Error fetching categories.");
    }
  };

  // Fetch subcategories
  const getSubCategoryList = async (categoryId) => {
    if (!token || !categoryId) {
      setSubCategoryData([]);
      setNavFormData([]);
      setFormEventsData([]);
      return;
    }
    try {
      const formdata = { formCategoryId: categoryId };
      const response = await post(
        `UserAuthentication/GetAllNavSubCategory`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response) {
        setSubCategoryData(response);
      } else {
        toast.error("Failed to fetch subcategories.");
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Error fetching subcategories.");
    }
  };

  // Fetch forms
  const getNavFormList = async (subCategoryId) => {
    if (!token || !subCategoryId) {
      setNavFormData([]);
      setFormEventsData([]);

      return;
    }
    try {
      const formdata = { id: subCategoryId };
      const response = await post(
        `UserAuthentication/GetAllNavForm`,
        formdata,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response) {
        setNavFormData(response);
      } else {
        toast.error("Failed to fetch forms.");
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Error fetching forms.");
    }
  };

  // Fetch notification templates
  const fetchAttachmentTemplates = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    try {
      const response = await get("Notification/attachmentTemplates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.success) {
        setTemplatesData(response?.notificationAttachmentTemplates);
      } else {
        toast.error("Failed to fetch templates.");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error fetching templates.");
    }
  };

  // Fetch form events
  const fetchFormEvents = async (formId) => {
    if (!token || !formId) {
      setFormEventsData([]);
      return;
    }
    try {
      const response = await get(
        `Notification/formEventTypes/?formId=${formId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response?.success && response?.notificationFormEvents) {
        setFormEventsData(response.notificationFormEvents);
      } else {
        toast.error("Failed to fetch form events.");
      }
    } catch (error) {
      console.error("Error fetching form events:", error);
      toast.error("Error fetching form events.");
    }
  };

  useEffect(() => {
    getCategories();
    fetchAttachmentTemplates();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      getSubCategoryList(selectedCategoryId);
    } else {
      setSubCategoryData([]);
      setNavFormData([]);
      setFormEventsData([]);

      setValue("FormSubCategoriesId", "");
      setValue("formId", "");
      setValue("notificationAttachmentTemplate.formId", "");
      setValue("notificationAttachmentTemplate.notificationFormEventId", "");
    }
  }, [selectedCategoryId, setValue]);

  useEffect(() => {
    if (selectedSubCategoryId) {
      getNavFormList(selectedSubCategoryId);
    } else {
      setNavFormData([]);
      setFormEventsData([]);

      setValue("formId", "");
      setValue("notificationAttachmentTemplate.formId", "");
      setValue("notificationAttachmentTemplate.notificationFormEventId", "");
    }
  }, [selectedSubCategoryId, setValue]);

  const handleCategoryChange = (selectedOption) => {
    const categoryId = selectedOption ? selectedOption.value : "";
    setValue("formCategoryId", categoryId);
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId(null);
    setValue("FormSubCategoriesId", "");
    setValue("formId", "");
    setValue("notificationAttachmentTemplate.formId", "");
    setValue("notificationAttachmentTemplate.notificationFormEventId", "");

    trigger("formCategoryId");
  };

  const handleSubCategoryChange = (selectedOption) => {
    const subCategoryId = selectedOption ? selectedOption.value : "";
    setValue("FormSubCategoriesId", subCategoryId);
    setSelectedSubCategoryId(subCategoryId);
    setValue("formId", "");
    setValue("notificationAttachmentTemplate.formId", "");
    setValue("notificationAttachmentTemplate.notificationFormEventId", "");

    trigger("FormSubCategoriesId");
  };

  const handleFormChange = (selectedOption) => {
    const formId = selectedOption ? selectedOption.value : "";
    setValue("formId", formId);
    setValue("notificationAttachmentTemplate.formId", formId);
    setValue("notificationAttachmentTemplate.notificationFormEventId", "");

    if (formId) {
      fetchFormEvents(formId);
    } else {
      setFormEventsData([]);
    }
    trigger("formId");
  };

  const handleFormEventChange = (selectedOption) => {
    const eventId = selectedOption ? selectedOption.value : "";
    setValue("notificationAttachmentTemplate.notificationFormEventId", eventId);
    trigger("notificationAttachmentTemplate.notificationFormEventId");
  };

  const handleFileTypeEventChange = (selectedOption) => {
    const fileTypeId = selectedOption ? selectedOption.value : "";
    setValue("notificationAttachmentTemplate.fileType", fileTypeId);
    trigger("notificationAttachmentTemplate.fileType");
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: "File Type",
      selector: (row) => row.fileType,
      sortable: true,
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0 border-0 custom-disabled-btn"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Assign"
          >
            <i className="fa fa-user-check"></i>
          </button>
          <button
            type="button"
            className="btn p-0 border-0 custom-disabled-btn"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Edit"
          >
            <i className="fa fa-pencil"></i>
          </button>
          <button
            type="button"
            className="btn p-0 border-0 custom-disabled-btn"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Delete"
          >
            <i className="fa fa-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const saveData = async (data) => {
    if (!token || !userId) {
      toast.error("Unauthorized. Please log in.");
      return;
    }

    // console.log(data);
    // return;

    try {
      const response = await post("Notification/saveAttachmentTemplate", data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.success) {
        toast.success("Success!");
        reset();
        setSelectedCategoryId(null);
        setSelectedSubCategoryId(null);
        await fetchAttachmentTemplates();
      } else {
        toast.error(response.message || "Failed to save template.");
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Error saving template.");
    }
  };

  const fileTypeOptions = [
    {
      id: "PDF",
      title: "PDF",
    },
    {
      id: "XLSX",
      title: "XLSX",
    },
    {
      id: "DOCX",
      title: "DOCX",
    },
    {
      id: "PNG",
      title: "PNG",
    },
  ];

  return (
    <div>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Attachment Template</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row gy-3 gx-2">
                <div className="col-md-4">
                  <label className="form-label">
                    Category<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="formCategoryId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={categoryData.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        onChange={handleCategoryChange}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  categoryData.find((c) => c.id === field.value)
                                    ?.name || "",
                              }
                            : null
                        }
                      />
                    )}
                  />
                  {errors.formCategoryId && (
                    <small className="text-danger">
                      {errors.formCategoryId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Sub Category<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="FormSubCategoriesId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={subCategoryData.map((subCategory) => ({
                          value: subCategory.id,
                          label: subCategory.name,
                        }))}
                        onChange={handleSubCategoryChange}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  subCategoryData.find(
                                    (sc) => sc.id === field.value
                                  )?.name || "",
                              }
                            : null
                        }
                      />
                    )}
                  />
                  {errors.FormSubCategoriesId && (
                    <small className="text-danger">
                      {errors.FormSubCategoriesId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Form<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="formId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={navFormData.map((form) => ({
                          value: form.id,
                          label: form.name,
                        }))}
                        onChange={handleFormChange}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  navFormData.find((f) => f.id === field.value)
                                    ?.name || "",
                              }
                            : null
                        }
                      />
                    )}
                  />
                  {errors.formId && (
                    <small className="text-danger">
                      {errors.formId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Form Event<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="notificationAttachmentTemplate.notificationFormEventId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={formEventsData.map((item) => ({
                          value: item.id,
                          label: item.title,
                        }))}
                        onChange={handleFormEventChange}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  formEventsData.find(
                                    (fe) => fe.id === field.value
                                  )?.title || "",
                              }
                            : null
                        }
                      />
                    )}
                  />
                  {errors.notificationAttachmentTemplate
                    ?.notificationFormEventId && (
                    <small className="text-danger">
                      {
                        errors.notificationAttachmentTemplate
                          .notificationFormEventId.message
                      }
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Title<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("notificationAttachmentTemplate.title", {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                  />
                  {errors.notificationAttachmentTemplate?.title && (
                    <small className="text-danger">
                      {errors.notificationAttachmentTemplate.title.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">File Type</label>
                  <Controller
                    name="notificationAttachmentTemplate.fileType"
                    control={control}
                    // rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={fileTypeOptions.map((item) => ({
                          value: item.id,
                          label: item.title,
                        }))}
                        onChange={handleFileTypeEventChange}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label:
                                  fileTypeOptions.find(
                                    (fe) => fe.id === field.value
                                  )?.title || "",
                              }
                            : null
                        }
                      />
                    )}
                  />
                  {errors.notificationAttachmentTemplate?.fileType && (
                    <small className="text-danger">
                      {errors.notificationAttachmentTemplate.fileType.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label">
                    Template<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="notificationAttachmentTemplate.templateHTML"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <div style={{ height: "270px" }}>
                        <ReactQuill
                          theme="snow"
                          modules={modules}
                          value={field.value}
                          onChange={field.onChange}
                          className="bg-white"
                          style={{ height: "200px" }}
                        />
                      </div>
                    )}
                  />
                  {errors.notificationAttachmentTemplate?.templateHTML && (
                    <small className="text-danger">
                      {
                        errors.notificationAttachmentTemplate.templateHTML
                          .message
                      }
                    </small>
                  )}
                </div>

                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="table-container fixed-last-column-table">
                <DataTable
                  columns={columns}
                  data={templatesData}
                  pagination
                  dense
                  customStyles={customTableStyles}
                  highlightOnHover
                  onChangePage={setCurrentPage}
                  onChangeRowsPerPage={setRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NotificationAttachmentTemplate;
